import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SectionHeader } from "./SectionHeader";

export default function TKDID() {
  return (
    <div className="min-h-screen">
      <ThematicAnalysisApproach />
      <KeyInsightsSection />
      <DesignRecommendationsSection />
      <IdeationSection />
    </div>
  );
}

function ThematicAnalysisApproach() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <motion.section
      ref={ref}
      style={{ y }}
      className="relative py-12 md:py-16 px-4 sm:px-8 md:px-24 lg:px-48 gradient-overlay"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex items-center gap-3 mb-16"
        >
          <SectionHeader number="" title="Thematic Analysis Approach" />
        </motion.div>

        {/* Design Rationale */}
        <DesignRationale2 />

        {/* Methodology */}
        <Methodology2 />
      </div>
    </motion.section>
  );
}

// Key Insights Section
function KeyInsightsSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const insights = [
    {
      number: "1",
      title: "Pricing Tools Accessibility",
      takeaway:
        "Many hosts struggle with non-intuitive pricing tools. There is a strong need for user-friendly systems that offer actionable insights, especially those incorporating real-time market trends and competitor data.",
      quote:
        "I don't know how much the price should be and what the current demand is around listings in my area.",
    },
    {
      number: "2",
      title: "Balancing Profitability with Competitiveness",
      takeaway:
        "Hosts find it challenging to balance competitive pricing with maintaining profitability. Essential costs—such as cleaning, maintenance, and mortgage payments—make pricing decisions more complex.",
      quote:
        "My primary challenge is balancing profitability with operational costs like cleaning and maintenance.",
    },
    {
      number: "3",
      title: "Impact of Seasonality and Location",
      takeaway:
        "Seasonality and location significantly influence pricing decisions. Hosts often struggle during off-peak seasons, where low occupancy makes it difficult to maintain reasonable rates.",
      quote:
        "During winter, it's very hard to set the right price. Low occupancy makes it difficult to balance the price and keep bookings.",
    },
    {
      number: "4",
      title: "Room Type Preference",
      takeaway:
        "Entire homes and apartments remain the most preferred accommodation types, especially in tourist destinations. This demand drives higher pricing for such listings.",
      quote:
        "We wanted to stay close to the main sights in Manhattan, but entire apartments were too expensive, so we chose a private room in Brooklyn.",
    },
    {
      number: "5",
      title: "Guest and Market Preferences",
      takeaway:
        "Hosts frequently modify their properties to meet guest expectations—such as furnishing spaces, offering amenities, or choosing high-demand neighborhoods—to stay competitive.",
      quote:
        "Most customers prefer furnished spaces, which is why we furnish our listings to stay competitive.",
    },
    {
      number: "6",
      title: "Market Challenges",
      takeaway:
        "Hosts consistently deal with fluctuating market conditions, including seasonal slowdowns and unpredictable demand changes. These fluctuations require frequent pricing adjustments.",
      quote:
        "During slow periods, we have to reduce prices to attract guests, especially in off-peak times.",
    },
  ];

  return (
    <motion.section
      ref={ref}
      style={{ y, opacity }}
      className="relative py-12 md:py-16 px-4 sm:px-8 md:px-24 lg:px-48 gradient-overlay"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader number="" title="Key Insights" />

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-lg md:text-xl text-gray-medium mb-16 max-w-4xl leading-relaxed mt-12"
        >
          Based on the combined findings from both the survey and the
          interviews, six key insights emerged as the most significant:
        </motion.p>

        {/* Insights Grid */}
        <div className="space-y-8">
          {insights.map((insight, index) => (
            <InsightCard key={index} insight={insight} index={index} />
          ))}
        </div>
      </div>
    </motion.section>
  );
}

// Insight Card Component
function InsightCard({ insight, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5], [0.9, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ scale, opacity }}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="glass-card rounded-2xl p-8 md:p-10 hover:border-[#FD5B38] transition-all duration-300"
    >
      {/* Header */}
      <div className="flex items-start gap-6 mb-6">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FD5B38] flex items-center justify-center">
          <span className="text-2xl text-white font-normal">
            {insight.number}
          </span>
        </div>
        <h3 className="text-2xl text-white font-normal pt-2">
          {insight.title}
        </h3>
      </div>

      {/* Content */}
      <div className="ml-0 md:ml-18 space-y-4">
        {/* Takeaway */}
        <div className="space-y-2">
          <h4 className="text-lg text-[#FD5B38] font-normal">Takeaway:</h4>
          <p className="text-lg text-gray-medium leading-relaxed">
            {insight.takeaway}
          </p>
        </div>

        {/* Quote */}
        <div className="quote-box rounded-2xl p-6 space-y-2">
          <h4 className="text-lg text-[#FD5B38] font-normal">Quote:</h4>
          <p className="text-lg text-gray-dark italic leading-relaxed">
            "{insight.quote}"
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Design Recommendations Section
function DesignRecommendationsSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);

  const recommendations = [
    {
      title: "Provide Intuitive Pricing Insights",
      items: [
        {
          subtitle: "Data-Driven Demand Analysis:",
          description:
            "Enable hosts to view real-time market demand, including occupancy trends, local events, and competitor pricing.",
        },
        {
          subtitle: "Clear Visualizations:",
          description:
            "Use graphs and charts to simplify complex pricing data, helping hosts quickly identify optimal pricing strategies.",
        },
        {
          subtitle: "Comparative Benchmarks:",
          description:
            "Allow hosts to compare their pricing with similar listings in their area to stay competitive.",
        },
      ],
      highlighted: false,
    },
    {
      title: "Efficient Cost Management Support",
      items: [
        {
          subtitle: "Expense Monitoring Tools:",
          description:
            "Include features to track operational costs such as cleaning fees, maintenance, and utilities.",
        },
        {
          subtitle: "Profitability Indicators:",
          description:
            "Show net profit margins for specific durations to help hosts make well-informed pricing and amenity investment decisions.",
        },
      ],
      highlighted: false,
    },
    {
      title: "Flexible and Seasonal Pricing Guidance",
      items: [
        {
          subtitle: "Dynamic Pricing Rules:",
          description:
            "Provide customizable rules for seasonal or event-based pricing to automate price adjustments during peak demand.",
        },
        {
          subtitle: "Historical Trends & Forecasting:",
          description:
            "Offer data-driven projections using past trends to help hosts anticipate future market shifts.",
        },
        {
          subtitle: "Manual Overrides:",
          description:
            "Allow hosts to easily modify automated pricing suggestions based on personal preferences or business strategies.",
        },
      ],
      highlighted: false,
    },
    {
      title: "Guest-Centric Customization Features",
      items: [
        {
          subtitle: "Tailored Recommendations:",
          description:
            "Suggest amenities and improvements such as Wi-Fi upgrades, family-friendly setups, or pet-friendly options based on guest feedback.",
        },
        {
          subtitle: "Privacy & Comfort Options:",
          description:
            "Highlight features like private entrances or security upgrades that align with guest preferences.",
        },
        {
          subtitle: "Guest Segmentation Insights:",
          description:
            "Help hosts analyze guest demographics—families, business travelers, etc.—to better tailor their offerings.",
        },
      ],
      highlighted: false,
    },
    {
      title: "User-Friendly Setup for Beginners",
      items: [
        {
          subtitle: "Step-by-Step Onboarding:",
          description:
            "Guide new hosts through listing setup, pricing configuration, and amenity selection.",
        },
        {
          subtitle: "Interactive Tutorials:",
          description:
            "Provide tutorials, tooltips, and explanations for complex features such as Smart Pricing.",
        },
        {
          subtitle: "Pre-Populated Templates:",
          description:
            "Offer templates for common stay types (weekends, holidays) to support novice users.",
        },
      ],
      highlighted: true,
    },
  ];

  return (
    <motion.section
      ref={ref}
      style={{ y }}
      className="relative py-24 md:py-32 px-4 sm:px-8 md:px-24 lg:px-48"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex items-center gap-3 mb-16"
        >
          <SectionHeader number="" title="Design Recommendations" />
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-lg md:text-xl text-gray-medium mb-16 max-w-5xl leading-relaxed"
        >
          Insights from user journey mapping and research activities highlight
          several opportunities to improve the experience of Airbnb hosts,
          especially around pricing strategies and guest satisfaction. The
          following design recommendations address these challenges:
        </motion.p>

        {/* Recommendations */}
        <div className="space-y-8">
          {recommendations.map((rec, index) => (
            <RecommendationCard
              key={index}
              recommendation={rec}
              index={index}
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
}

// Recommendation Card Component
function RecommendationCard({ recommendation, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      className={`${
        recommendation.highlighted ? "glass-card-highlighted" : "glass-card"
      } rounded-2xl p-8 md:p-10 hover:border-[#FD5B38] transition-all duration-300`}
    >
      <h3
        className={`text-3xl font-normal mb-8 ${
          recommendation.highlighted ? "text-white" : "text-white"
        }`}
      >
        {recommendation.title}
      </h3>

      <div className="space-y-6">
        {recommendation.items.map((item, idx) => (
          <div key={idx} className="border-l-4 border-[#FD5B38] pl-7 space-y-3">
            <h4
              className={`text-xl font-normal ${
                recommendation.highlighted ? "text-white" : "text-[#FD5B38]"
              }`}
            >
              {item.subtitle}
            </h4>
            <p
              className={`text-lg leading-relaxed ${
                recommendation.highlighted
                  ? "text-gray-light"
                  : "text-gray-medium"
              }`}
            >
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// Ideation Section
function IdeationSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <motion.section
      ref={ref}
      style={{ y }}
      className="relative py-12 md:py-16 px-4 sm:px-8 md:px-24 lg:px-48 gradient-overlay"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex items-center gap-3 mb-16"
        >
          <SectionHeader number="" title="Ideation" />
        </motion.div>

        {/* Concept Development */}
        <ConceptDevelopment />

        {/* Design Rationale */}
        <DesignRationale />

        {/* Methodology */}
        <Methodology />
      </div>
    </motion.section>
  );
}

// Concept Development Component
function ConceptDevelopment() {
  const bulletPoints = [
    "Suggests optimal pricing",
    "Allows manual overrides",
    "Tracks expenses",
    "Clearly explains the reasoning behind pricing suggestions",
  ];

  const gaps = [
    "Transparency",
    "Customization",
    "Contextual support for new or part-time hosts",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="mb-20"
    >
      <h3 className="text-3xl text-white font-normal mb-8">
        Concept Development
      </h3>

      <div className="space-y-8">
        {/* Intro Paragraph */}
        <p className="text-lg md:text-xl text-gray-medium leading-relaxed">
          To identify the strongest solutions, an affinity mapping exercise was
          used to organize and prioritize design recommendations. Feedback from
          interview participants highlighted a strong need for a tool that:
        </p>

        {/* Bullet Points */}
        <div className="ml-6 space-y-3">
          {bulletPoints.map((point, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <span className="text-2xl text-[#FD5B38] leading-none mt-1">
                •
              </span>
              <p className="text-lg md:text-xl text-gray-medium leading-relaxed pt-1">
                {point}
              </p>
            </div>
          ))}
        </div>

        {/* Gap Analysis */}
        <div className="glass-card rounded-2xl p-8 space-y-6">
          <p className="text-lg md:text-xl text-gray-medium leading-relaxed">
            A comparative review of tools like Airbnb Smart Pricing, Beyond
            Pricing, and PriceLabs showed that although these platforms offer
            automated pricing, they often lack:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {gaps.map((gap, idx) => (
              <div
                key={idx}
                className="rounded-2xl item-border border-[#FD5B38] bg-black/20 p-6"
              >
                <p className="text-xl text-[#FD5B38] leading-relaxed">{gap}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Solution */}
        <div className="glass-card-highlighted rounded-2xl p-8">
          <p className="text-lg md:text-xl text-gray-light leading-relaxed">
            The solution is <span className="font-bold text-white">Metrix</span>
            , an intelligent pricing application that uses market variables and
            host-specific data to provide dynamic pricing recommendations, cost
            analysis, and seasonal forecasting. Its primary goal is to enhance
            revenue predictability, simplify decision-making, and increase host
            confidence.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Design Rationale Component
function DesignRationale() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="mb-20"
    >
      <div className="glass-card rounded-2xl p-8 md:p-10 space-y-6">
        <h3 className="text-3xl text-white font-normal">Design Rationale</h3>

        <p className="text-lg md:text-xl text-gray-medium leading-relaxed">
          After evaluating multiple "How Might We" possibilities, the chosen
          direction was to empower Airbnb hosts with dynamic pricing controls
          powered by machine learning. This approach helps build trust and
          confidence in pricing decisions while providing deeper insight into
          market trends.
        </p>

        <p className="text-lg md:text-xl text-gray-medium leading-relaxed">
          Although the initial concept was a browser plugin, research revealed
          that most hosts manage listings via mobile. Therefore, the first phase
          focuses on developing a mobile application with customization and
          manual control options, with plugin integration planned for later
          stages.
        </p>
      </div>
    </motion.div>
  );
}

// Design Rationale Component
function DesignRationale2() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="lg:mb-16 md:mb-12 mb-8"
    >
      <p className="text-lg md:text-xl text-gray-medium leading-relaxed">
        Interview data were analyzed using a structured thematic analysis
        process consisting of five key steps:
      </p>
    </motion.div>
  );
}

// Methodology Component
function Methodology() {
  const steps = [
    {
      number: "1",
      title: "Machine Learning-Based Pricing Recommendation",
      description: "Metrix analyzes multiple variables:",
      items: [
        {
          subtitle: "Structured Inputs:",
          description:
            "Property type, location, bedrooms, amenities, occupancy rate.",
        },
        {
          subtitle: "Unstructured Inputs:",
          description:
            "Guest reviews processed using NLP to generate sentiment scores.",
        },
        {
          subtitle: "Seasonal & Event Signals:",
          description:
            "External datasets for holidays, festivals, and local events.",
        },
      ],
      footer:
        "The model uses Random Forest and Gradient Boosting Regression, chosen for handling complex Airbnb datasets. The system retrains regularly to maintain accuracy.",
    },
    {
      number: "2",
      title: "Training Data Sources",
      description: "The model combines host inputs with external databases:",
      listItems: [
        {
          label: "Public Airbnb Data:",
          text: "Historical prices, listing features, occupancy trends",
        },
        {
          label: "Interview & Survey Insights:",
          text: "Used to identify key decision points and override behaviors",
        },
        {
          label: "Event & Seasonality Data:",
          text: "Sourced from Google Events and tourism boards",
        },
        {
          label: "Guest Review Text:",
          text: "NLP-based sentiment scoring for quality assessment",
        },
      ],
      footer:
        "This mix ensures the model adapts to predictable patterns and real-time market fluctuations.",
    },
    {
      number: "3",
      title: "Price Tier Recommendations",
      description:
        "Metrix offers tiered price suggestions to support different host goals:",
      cards: [
        {
          subtitle: "Recommended Price:",
          description: "Balanced option optimizing both occupancy and revenue",
        },
        {
          subtitle: "Conservative Price:",
          description:
            "Lower pricing for hosts wanting higher bookings or during low-demand seasons",
        },
        {
          subtitle: "Aggressive Price:",
          description:
            "Higher pricing based on positive reviews or peak seasons",
        },
      ],
      footer:
        "Each recommendation includes a factor breakdown to provide full transparency.",
      highlighted: true,
    },
    {
      number: "4",
      title: "Price Verification & Adjustment Logic",
      description: "AI-driven mechanisms refine and validate pricing:",
      items: [
        {
          subtitle: "Review Sentiment Weighting:",
          description: "Prices adjust based on guest sentiment",
        },
        {
          subtitle: "Temporal Analysis:",
          description:
            "Adjustments based on lead time, booking windows, and seasonal changes",
        },
      ],
      footer: "This ensures prices evolve with market conditions.",
    },
    {
      number: "5",
      title: "Ethical, Practical & Usability Considerations",
      description: "Metrix prioritizes accuracy, transparency, and user trust:",
      listItems: [
        {
          label: "Host Control:",
          text: "Full ability to override automated suggestions",
        },
        {
          label: "Visual Transparency:",
          text: "Dashboards showing how prices were generated",
        },
        {
          label: "Data Security:",
          text: "Encrypted storage for all host data",
        },
        {
          label: "Bias Auditing:",
          text: "Regular checks against unfair pricing biases",
        },
      ],
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="space-y-12"
    >
      <h3 className="text-3xl text-white font-normal">
        Methodology – Pricing Model Development & Data Sources
      </h3>

      <p className="text-lg md:text-xl text-gray-medium leading-relaxed">
        Metrix leverages machine learning to help hosts optimize listing prices
        by combining structured data, guest sentiment, and local market signals.
        The pricing model uses a multi-layered methodology informed by primary
        research and secondary datasets.
      </p>

      <div className="space-y-8">
        {steps.map((step, index) => (
          <MethodologyStep key={index} step={step} />
        ))}
      </div>
    </motion.div>
  );
}

function Methodology2() {
  const steps = [
    {
      number: "1",
      title: "Transcription and Familiarization",
      description:
        "All interviews were transcribed verbatim. The transcripts were reviewed multiple times to fully understand the content and context.",
    },
    {
      number: "2",
      title: "Open Coding",
      description: `Important statements and recurring ideas were tagged with descriptive codes such as "seasonal pricing challenges," "impact of guest feedback," and "distrust in automated tools."`,
    },
    {
      number: "3",
      title: "Theme Development",
      description:
        "Codes were organized into broader themes that reflected the core strategies, concerns, and experiences shared across participants.",
    },
    {
      number: "4",
      title: "Thematic Refinement",
      description:
        "Themes were refined and adjusted to ensure they accurately represented the content and aligned with the study's objectives.",
    },
    {
      number: "5",
      title: "Presentation",
      description:
        "Finalized themes were synthesized into clear, actionable insights, supported by direct participant quotes to enhance qualitative depth.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="space-y-12"
    >
      <div className="space-y-8">
        {steps.map((step, index) => (
          <MethodologyStep2 key={index} step={step} />
        ))}
        <div className="glass-card-highlighted rounded-2xl p-8 md:p-10 text-lg md:text-xl leading-relaxed text-gray-medium">
          This method ensured a thorough and reliable analysis, revealing
          meaningful patterns in the pricing challenges and strategies of Airbnb
          hosts.
        </div>
      </div>
    </motion.div>
  );
}

// Methodology Step Component
function MethodologyStep({ step }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className={`${
        step.highlighted ? "glass-card-highlighted" : "glass-card"
      } rounded-2xl p-8 md:p-10`}
    >
      {/* Header */}
      <div className="flex items-start gap-6 mb-8">
        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#FD5B38] flex items-center justify-center">
          <span className="text-3xl text-white font-normal">{step.number}</span>
        </div>
        <h4 className="text-3xl text-white font-normal pt-3">{step.title}</h4>
      </div>

      {/* Content */}
      <div className="ml-0 md:ml-22 space-y-6">
        <p
          className={`text-lg md:text-xl leading-relaxed ${
            step.highlighted ? "text-gray-light" : "text-gray-medium"
          }`}
        >
          {step.description}
        </p>

        {/* Items with border */}
        {step.items && (
          <div className="space-y-4">
            {step.items.map((item, idx) => (
              <div
                key={idx}
                className="border-l-4 border-[#FD5B38] pl-7 space-y-2"
              >
                <h5
                  className={`text-xl font-normal ${
                    step.highlighted ? "text-white" : "text-[#FD5B38]"
                  }`}
                >
                  {item.subtitle}
                </h5>
                <p
                  className={`text-lg leading-relaxed ${
                    step.highlighted ? "text-gray-light" : "text-gray-medium"
                  }`}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* List items with bold labels */}
        {step.listItems && (
          <div className="ml-6 space-y-3">
            {step.listItems.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="text-2xl text-[#FD5B38] leading-none mt-1">
                  •
                </span>
                <p
                  className={`text-lg md:text-xl leading-relaxed pt-1 ${
                    step.highlighted ? "text-gray-light" : "text-gray-medium"
                  }`}
                >
                  <span className="font-bold text-white">{item.label}</span>{" "}
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Cards */}
        {step.cards && (
          <div className="space-y-4">
            {step.cards.map((card, idx) => (
              <div key={idx} className="rounded-2xl bg-black/20 p-6 space-y-2">
                <h5 className="text-xl text-[#FD5B38] font-normal">
                  {card.subtitle}
                </h5>
                <p className="text-lg text-gray-light leading-relaxed">
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {step.footer && (
          <p
            className={`text-lg md:text-xl leading-relaxed ${
              step.highlighted ? "text-gray-light" : "text-gray-medium"
            }`}
          >
            {step.footer}
          </p>
        )}
      </div>
    </motion.div>
  );
}

function MethodologyStep2({ step }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="glass-card rounded-2xl"
    >
      {/* Header */}
      <div className="flex items-center gap-6 my-4 px-8">
        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#FD5B38] flex items-center justify-center">
          <span className="text-3xl text-white font-normal">{step.number}</span>
        </div>
        {/* Content */}
        <div className="ml-0 md:ml-22 space-y-6">
          <h4 className="text-3xl text-white font-normal pt-3">{step.title}</h4>
          <p
            className={`text-lg md:text-xl leading-relaxed text-gray-medium`}
          >
            {step.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
